import { Component, ViewEncapsulation, ChangeDetectionStrategy, Input } from '@angular/core';
import isEmpty from 'lodash-es/isEmpty';

import {
  HotelValidationResponse, HotelCancelationPolicies, ReservedRoom
} from '@booking/entities/hotel-validation.response';
import { isNullOrUndefined } from '@app/util/util';
import { RefundableTypes } from '@booking/entities/refundable-types';
import {
  TypographyFontFamily, TypographyFontSize, TypographyColor
} from '@design-system/components/atoms/typography/entities/typography.types';
import { AccordionType, AccordionSize } from '@design-system/components/templates/accordion/entities/accordion.types';

@Component({
  selector: 'app-hotels-policies-data',
  templateUrl: './hotels-policies-data.component.html',
  styleUrls: [
    '../policies-detail/policies-detail.component.sass',
  ],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class HotelsPoliciesDataComponent {

  @Input() hotelValidation: HotelValidationResponse;
  @Input() isUserHistory = false;

  public collapseCompleteHotelTerms: boolean = false;

  public readonly fontFamily = TypographyFontFamily;
  public readonly fontSize = TypographyFontSize;
  public readonly fontColor = TypographyColor;

  public readonly accordionType = AccordionType.Card;
  public readonly accordionSize = AccordionSize.SM;

  constructor() { }

  get hasHotelPolicies(): boolean {
    return !isEmpty(this.hotelValidation) && this.hasCancelPolicies;
  }

  get hasCancelPolicies(): boolean {
    return !!this.hotelValidation?.CancellationPolicies?.length;
  }

  get rooms(): ReservedRoom[] {
    return this.hasHotelPolicies ? this.hotelValidation?.HotelReservation?.Rooms : [];
  }

  public roomCancelationPolicies(index: number): HotelCancelationPolicies[] {
    const cancelationPolicies = this.hotelValidation.CancellationPolicies;
    const policiesByRoom = cancelationPolicies.filter(policy => policy.RoomIndex === (index + 1));
    const policiesByNoRoom = cancelationPolicies.filter(policy => !policy.RoomIndex);
    return policiesByRoom && policiesByRoom.length ? policiesByRoom : policiesByNoRoom && policiesByNoRoom.length ?
      policiesByNoRoom : cancelationPolicies && cancelationPolicies.length ? [cancelationPolicies[0]] : [];
  }

  public roomIndexExists(roomIndex: number): boolean {
    return !isNaN(roomIndex);
  }

  public toggleHotelTerms(): void {
    this.collapseCompleteHotelTerms = !this.collapseCompleteHotelTerms;
  }

  public getSimpleDate(date: string): string {
    const utc = /(\d{4})-(\d{2})-(\d{2})T(\d{2})\:(\d{2})\:(\d{2})(?:\.\d+)?Z?/;
    const partsUTC = date.match(utc);
    let stringDate = '';
    if (isNullOrUndefined(partsUTC)) {
      const iso = /(\d{4})-(\d{2})-(\d{2})T(\d{2})\:(\d{2})\:(\d{2})([zZ]|([+-](\d{2})\:(\d{2})))/;
      const parts = date.match(iso);
      if (isNullOrUndefined(parts)) return date;
      else {
        stringDate = this.getDateFormat(date, parts[2], parts[1], parts[3], parts[4], parts[5], parts[6]);
      }
    } else {
      stringDate = this.getDateFormat(date, partsUTC[2], partsUTC[1], partsUTC[3], partsUTC[4], partsUTC[5], partsUTC[6]);
    }
    return stringDate;
  }

  public showCancellationAmount(policies: HotelCancelationPolicies, index: number): boolean {
    const today = new Date().setHours(0, 0, 0, 0);
    const dateFrom = new Date(policies.DateFrom).setHours(0, 0, 0, 0);
    const { Rooms } = this.hotelValidation.HotelReservation;
    const { RefundableType } = Rooms[index];
    return dateFrom > today && RefundableType === RefundableTypes.REFUNDABLE;
  }

  private getDateFormat(
    date: string, stringMonths: string, stringYear: string, stringDays: string,
    stringHours: string, stringMinutes: string, stringSeconds: string
  ): string {
    let months, year, days, hours, minutes, seconds = 0;
    year = Number(stringYear);
    if (!isNullOrUndefined(stringMonths) && !isNullOrUndefined(stringDays)) {
      months = Number(stringMonths);
      days = Number(stringDays);
    }
    if (!isNullOrUndefined(stringHours) && !isNullOrUndefined(stringMinutes)) {
      hours = Number(stringHours);
      minutes = Number(stringMinutes);
      if (!isNullOrUndefined(stringSeconds)) seconds = Number(stringSeconds);
    }
    if (!year || !months || !days) return date;
    else {
      const stringH = hours < 10 ? `0${hours}` : hours;
      const stringM = minutes < 10 ? `0${minutes}` : minutes;
      const stringS = seconds < 10 ? `0${seconds}` : seconds;
      const stringMTH = months < 10 ? `0${months}` : months;
      const stringDay = days < 10 ? `0${days}` : days;
      return `${year}-${stringMTH}-${stringDay}T${stringH}:${stringM}:${stringS}`;
    }
  }
}
